import React, { useMemo, useRef, useState } from 'react';
import {
  FileSpreadsheet, Upload, Loader2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft,
} from 'lucide-react';
import api from '@/lib/axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * Generic CSV/XLSX importer for the "students" entity.
 *
 * Flow:
 *   1. Upload file              → POST /imports/generic/preview
 *   2. Map source columns →     (local state only)
 *      target fields
 *   3. Confirm                  → POST /imports/generic/commit
 *
 * The dialog is opened per-tenant from the Mandantenverwaltung.
 */
export default function GenericImportDialog({ open, onOpenChange, tenant, onImported, target: initialTarget }) {
  const [phase, setPhase] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({}); // { sourceHeader: targetField }
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState(initialTarget || 'students');
  const inputRef = useRef();

  const targetLabels = {
    students: 'Schüler',
    teachers: 'Lehrer',
    subjects: 'Fächer',
  };

  const reset = () => {
    setPhase('upload'); setPreview(null); setMapping({});
    setResult(null); setError(null); setFile(null);
    setTarget(initialTarget || 'students');
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const runPreview = async (f) => {
    if (!f) return;
    setFile(f); setError(null); setPhase('uploading');
    try {
      const form = new FormData();
      form.append('file', f);
      form.append('target', target);
      const res = await api.post('/imports/generic/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      // Auto-suggest mapping: case-insensitive match on common german/english labels
      const auto = {};
      const aliasesByTarget = {
        students: {
          first_name: ['vorname', 'first name', 'firstname', 'given name'],
          last_name: ['nachname', 'last name', 'lastname', 'surname', 'familienname'],
          date_of_birth: ['geburtsdatum', 'geburtstag', 'date of birth', 'dob', 'geb', 'geb.datum'],
          gender: ['geschlecht', 'sex', 'gender', 'm/w', 'm/f'],
          email: ['email', 'e-mail', 'mail'],
          phone: ['telefon', 'tel', 'phone', 'festnetz'],
          mobile: ['mobil', 'handy', 'mobile'],
          address: ['adresse', 'straße', 'strasse', 'address', 'street'],
          postal_code: ['plz', 'postleitzahl', 'zip', 'postal'],
          city: ['ort', 'stadt', 'city', 'town'],
          notes: ['notizen', 'bemerkung', 'notes', 'anmerkung'],
          imikel_kassenzeichen: ['kassenzeichen', 'konto_heute_datakonten'],
        },
        teachers: {
          first_name: ['vorname', 'first name', 'firstname'],
          last_name: ['nachname', 'last name', 'lastname'],
          email: ['email', 'e-mail', 'mail'],
          phone: ['telefon', 'tel', 'phone'],
          address: ['adresse', 'straße', 'strasse', 'address'],
          postal_code: ['plz', 'postleitzahl', 'zip'],
          city: ['ort', 'stadt', 'city'],
          qualifications: ['qualifikation', 'fach', 'faecher', 'fächer', 'abschluss'],
          hourly_rate: ['stundensatz', 'honorar', 'rate', 'hourly'],
          notes: ['notiz', 'bemerkung', 'notes'],
        },
        subjects: {
          name: ['name', 'fach', 'fächer', 'subject', 'bezeichnung'],
          category: ['kategorie', 'instrument', 'category', 'typ'],
          description: ['beschreibung', 'description', 'info'],
        },
      };
      const aliases = aliasesByTarget[target] || aliasesByTarget.students;
      for (const h of res.data.headers) {
        const lower = (h || '').toLowerCase().trim();
        for (const [field, names] of Object.entries(aliases)) {
          if (names.some((n) => lower.includes(n))) { auto[h] = field; break; }
        }
      }
      setMapping(auto);
      setPhase('mapping');
    } catch (err) {
      setError(err.response?.data?.detail || 'Datei konnte nicht gelesen werden');
      setPhase('upload');
    }
  };

  const targetOptions = useMemo(
    () => Object.entries(preview?.target_fields || {}),
    [preview],
  );

  const requiredFields = {
    students: ['first_name', 'last_name'],
    teachers: ['first_name', 'last_name'],
    subjects: ['name'],
  }[target] || [];
  const mappedValues = Object.values(mapping);
  const requiredOk = requiredFields.every((r) => mappedValues.includes(r));

  const runCommit = async () => {
    setPhase('committing'); setError(null);
    try {
      const form = new FormData();
      form.append('token', preview.token);
      form.append('tenant_id', tenant.id);
      form.append('target', target);
      const cleanMap = Object.fromEntries(
        Object.entries(mapping).filter(([_, v]) => v),
      );
      form.append('mapping', JSON.stringify(cleanMap));
      const res = await api.post('/imports/generic/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setPhase('done');
      toast.success(`${res.data.inserted} ${targetLabels[target] || 'Datensätze'} importiert`);
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import fehlgeschlagen');
      setPhase('mapping');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="generic-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-brand-red" strokeWidth={1.5} />
            {targetLabels[target] || 'Datensätze'} aus CSV/XLSX importieren
          </DialogTitle>
          <DialogDescription>
            Mandant: <span className="font-semibold">{tenant?.name}</span> ({tenant?.slug})
          </DialogDescription>
        </DialogHeader>

        {phase === 'upload' && (
          <div className="py-4">
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 block">
                Was soll importiert werden?
              </label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="w-full" data-testid="generic-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="students">Schüler</SelectItem>
                  <SelectItem value="teachers">Lehrer</SelectItem>
                  <SelectItem value="subjects">Fächer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                {target === 'teachers' && 'Pflichtfelder: Vorname, Nachname. Ohne E-Mail wird automatisch eine Platzhalter-Adresse erzeugt.'}
                {target === 'subjects' && 'Pflichtfeld: Fach-Name. Fächer sind pro Mandant eindeutig.'}
                {target === 'students' && 'Pflichtfelder: Vorname, Nachname. Duplikate werden per (Vorname, Nachname, Geburtsdatum) erkannt.'}
              </p>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Wähle eine CSV oder XLSX. Im nächsten Schritt ordnest Du die Spalten den
              Zielfeldern zu.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              data-testid="generic-file-input"
              onChange={(e) => runPreview(e.target.files?.[0])}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              className="w-full"
              data-testid="generic-pick-file"
            >
              <Upload className="w-4 h-4 mr-2" /> Datei wählen…
            </Button>
            {error && (
              <p className="mt-3 text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {error}
              </p>
            )}
          </div>
        )}

        {phase === 'uploading' && (
          <Loading text="Datei wird gelesen…" />
        )}

        {phase === 'mapping' && preview && (
          <div data-testid="generic-mapping">
            <p className="text-sm text-slate-600 mb-3">
              {preview.total_rows.toLocaleString('de-DE')} Zeilen in{' '}
              <span className="font-mono text-xs">{file?.name}</span>. Ordne jede
              Spalte einem Zielfeld zu oder lass sie leer, um sie zu ignorieren.
            </p>

            <div className="border rounded-md overflow-hidden">
              <div className="grid grid-cols-12 bg-slate-50 border-b text-[11px] font-semibold uppercase text-slate-600">
                <div className="col-span-5 p-2">Quell-Spalte</div>
                <div className="col-span-1 p-2 text-center">→</div>
                <div className="col-span-6 p-2">Zielfeld</div>
              </div>
                    {preview.headers.map((h) => (
                  <div key={h} className="grid grid-cols-12 border-b last:border-b-0 items-center">
                  <div className="col-span-5 p-2">
                    <div className="font-medium text-sm text-slate-800 truncate">{h || <em className="text-slate-400">(leer)</em>}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      z.B. {preview.sample_rows[0]?.[h] || '—'}
                    </div>
                  </div>
                  <div className="col-span-1 p-2 text-slate-400 text-center">→</div>
                  <div className="col-span-6 p-2">
                    <Select
                      value={mapping[h] || '__none__'}
                      onValueChange={(v) =>
                        setMapping((m) => ({ ...m, [h]: v === '__none__' ? '' : v }))
                      }
                    >
                      <SelectTrigger
                        className="h-8 text-sm"
                        data-testid={`generic-map-${h}`}
                      >
                        <SelectValue placeholder="(ignorieren)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          <span className="text-slate-400">(ignorieren)</span>
                        </SelectItem>
                        {targetOptions.map(([k, label]) => (
                          <SelectItem key={k} value={k}>
                            {label} {requiredFields.includes(k) ? '(Pflicht)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                ))}
            </div>

            {!requiredOk && (
              <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {target === 'subjects'
                  ? 'Das Pflichtfeld „Fach-Name" muss zugeordnet sein.'
                  : 'Vorname und Nachname müssen zugeordnet sein.'}
              </p>
            )}

            {error && (
              <p className="mt-3 text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {error}
              </p>
            )}

            <div className="mt-4 flex justify-between">
              <Button variant="outline" onClick={() => setPhase('upload')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
              </Button>
              <Button
                onClick={runCommit}
                disabled={!requiredOk}
                data-testid="generic-commit"
              >
                {preview.total_rows.toLocaleString('de-DE')} {targetLabels[target] || 'Datensätze'} importieren
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {phase === 'committing' && (
          <Loading text="Import läuft…" />
        )}

        {phase === 'done' && result && (
          <div className="py-2" data-testid="generic-done">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
              <CheckCircle2 className="w-5 h-5" />
              Import abgeschlossen
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <StatTile label="Importiert" value={result.inserted} accent />
              <StatTile label="Duplikate übersprungen" value={result.skipped_duplicates} />
              <StatTile label="Ungültig übersprungen" value={result.skipped_invalid} warn={result.skipped_invalid > 0} />
            </div>
            {result.errors.length > 0 && (
              <div className="mt-3 border border-amber-200 bg-amber-50 rounded p-3">
                <p className="text-xs font-semibold text-amber-900 mb-1">Hinweise:</p>
                <ul className="text-[11px] text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
                  {result.errors.map((e, i) => <li key={`${i}-${e}`}>{e}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button onClick={() => close(false)}>Schließen</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Loading = ({ text }) => (
  <div className="py-10 flex flex-col items-center gap-2 text-slate-500">
    <Loader2 className="w-7 h-7 animate-spin" />
    <p className="text-sm">{text}</p>
  </div>
);

const StatTile = ({ label, value, accent, warn }) => (
  <div className={[
    'rounded-md border px-3 py-2',
    accent ? 'border-brand-red/20 bg-brand-red/5'
      : warn ? 'border-amber-200 bg-brand-red/5'
        : 'border-slate-200 bg-slate-50',
  ].join(' ')}>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg font-semibold ${accent ? 'text-brand-red' : 'text-slate-800'}`}>
      {(value ?? 0).toLocaleString('de-DE')}
    </p>
  </div>
);
